import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import RequireBillingRole from './components/RequireBillingRole';
import CreditsEngineService, { toNumber } from '../../../../services/CreditsEngineService';
import TablePaginationBar from '../../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../../hooks';
import HelpIcon from '../../../../components/help/HelpIcon';
import { isDeprecatedClaudeModel, MIN_CLAUDE_VERSION } from '../../../../utils/llmModels';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

// capture (agent-runtime, chart-data, summarize-title) busca el precio con
// provider "bedrock" + el ID exacto con el que se invoca Bedrock. Una fila con
// otra clave nunca se usa y el turno se cobra con el precio de respaldo -- por
// eso se valida acá en vez de aceptar texto libre.
export const PRICING_PROVIDER = 'bedrock';
export const BEDROCK_MODEL_ID_PATTERN = /^((us|eu|apac|jp|au|global)\.)?[a-z0-9-]+\.[a-z0-9.-]+(:\d+)?$/;

export const validateModelPricingKey = (provider, model, { active = true } = {}) => {
  if (provider.trim() !== PRICING_PROVIDER) {
    return `El proveedor debe ser "${PRICING_PROVIDER}".`;
  }
  if (!BEDROCK_MODEL_ID_PATTERN.test(model.trim())) {
    return 'El modelo debe ser el ID con el que se invoca Bedrock, p. ej. us.anthropic.claude-sonnet-5-5.';
  }
  // Mismo criterio que credits-engine: un modelo en desuso no se puede dar
  // de alta ACTIVO (desactivado sí, p. ej. para conservar su precio).
  if (active && isDeprecatedClaudeModel(model.trim())) {
    return `Ese modelo está en desuso: la versión mínima de Claude en la plataforma es ${MIN_CLAUDE_VERSION}.`;
  }
  return null;
};

// Espejo de PAID_PLAN_MODEL_FAMILIES en whisperbi-credits-engine/src/credits-engine/
// pricing_resolver.py: familias exclusivas de planes de pago cuando la fila no
// dice otra cosa (requires_paid_plan). El bloqueo real lo hace credits-engine
// en `reserve` (MODEL_PLAN_REQUIRED).
const PAID_PLAN_MODEL_FAMILIES = ['claude-opus', 'claude-fable'];

/**
 * Versión nueva de un precio con `active` invertido (no hay DELETE: la tabla
 * es versionada). Debe conservar todo lo demás de la vigente: antes se
 * perdían los costos de caché/razonamiento al activar/desactivar.
 */
export const toggledPricingPayload = (item) => ({
  provider: item.provider,
  model: item.model,
  input_cost_per_1k_tokens: toNumber(item.input_cost_per_1k_tokens),
  output_cost_per_1k_tokens: toNumber(item.output_cost_per_1k_tokens),
  ...(item.cached_input_cost_per_1k_tokens != null ? { cached_input_cost_per_1k_tokens: toNumber(item.cached_input_cost_per_1k_tokens) } : {}),
  ...(item.cache_write_cost_per_1k_tokens != null ? { cache_write_cost_per_1k_tokens: toNumber(item.cache_write_cost_per_1k_tokens) } : {}),
  ...(item.reasoning_token_cost_per_1k_tokens != null ? { reasoning_token_cost_per_1k_tokens: toNumber(item.reasoning_token_cost_per_1k_tokens) } : {}),
  ...(item.requires_paid_plan != null ? { requires_paid_plan: Boolean(item.requires_paid_plan) } : {}),
  currency: item.currency || 'USD',
  active: !(item.active !== false)
});

export const modelRequiresPaidPlan = (model, item) => (
  item?.requires_paid_plan != null
    ? Boolean(item.requires_paid_plan)
    : PAID_PLAN_MODEL_FAMILIES.some((family) => String(model || '').includes(family))
);

const emptyForm = {
  provider: PRICING_PROVIDER,
  model: '',
  input_cost_per_1k_tokens: '',
  output_cost_per_1k_tokens: '',
  cached_input_cost_per_1k_tokens: '',
  cache_write_cost_per_1k_tokens: '',
  reasoning_token_cost_per_1k_tokens: '',
  currency: 'USD',
  active: true,
  requires_paid_plan: null
};

const BillingPricingContent = () => {
  const [pricing, setPricing] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [toggling, setToggling] = useState(null);

  const [cursor, setCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Versionado por effective_from -- mostrar todas las versiones, más
  // reciente primero (colapsar a "solo la vigente" queda como mejora futura).
  const porVersionDescendente = (filas) =>
    [...filas].sort((a, b) => String(b.effective_from || '').localeCompare(String(a.effective_from || '')));

  // Paginación con cursor del backend: `load` trae la primera página y
  // `loadMore` va apilando las siguientes. El pager numerado opera sobre lo
  // acumulado; useTablePagination llama a `loadMore` cuando el usuario
  // entra a la última página cargada y todavía queda cursor.
  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { items, nextCursor } = await CreditsEngineService.listModelPricing();
      setPricing(porVersionDescendente(items));
      setCursor(nextCursor);
    } catch (err) {
      setError(err.message || 'No fue posible obtener el pricing de modelos.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const { items, nextCursor } = await CreditsEngineService.listModelPricing({ cursor });
      setPricing((prev) => porVersionDescendente([...prev, ...items]));
      setCursor(nextCursor);
    } catch (err) {
      setError(err.message || 'No fue posible obtener más versiones de pricing.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [cursor, isLoadingMore]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setIsEditing(false);
    setFormValues(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setIsEditing(true);
    setFormValues({
      provider: item.provider || '',
      model: item.model || '',
      input_cost_per_1k_tokens: String(toNumber(item.input_cost_per_1k_tokens)),
      output_cost_per_1k_tokens: String(toNumber(item.output_cost_per_1k_tokens)),
      cached_input_cost_per_1k_tokens: item.cached_input_cost_per_1k_tokens != null ? String(toNumber(item.cached_input_cost_per_1k_tokens)) : '',
      cache_write_cost_per_1k_tokens: item.cache_write_cost_per_1k_tokens != null ? String(toNumber(item.cache_write_cost_per_1k_tokens)) : '',
      reasoning_token_cost_per_1k_tokens: item.reasoning_token_cost_per_1k_tokens != null ? String(toNumber(item.reasoning_token_cost_per_1k_tokens)) : '',
      currency: item.currency || 'USD',
      active: item.active !== false,
      requires_paid_plan: modelRequiresPaidPlan(item.model, item)
    });
    setFormOpen(true);
  };

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!formValues.provider.trim() || !formValues.model.trim()) {
      setSnackbar({ open: true, message: 'El proveedor y el modelo son requeridos.', severity: 'error' });
      return;
    }
    const keyError = isEditing ? null : validateModelPricingKey(formValues.provider, formValues.model, { active: formValues.active });
    if (keyError) {
      setSnackbar({ open: true, message: keyError, severity: 'error' });
      return;
    }
    if (formValues.input_cost_per_1k_tokens === '' || formValues.output_cost_per_1k_tokens === '') {
      setSnackbar({ open: true, message: 'Los precios de input/output son requeridos.', severity: 'error' });
      return;
    }
    try {
      setIsSaving(true);
      const payload = {
        provider: formValues.provider.trim(),
        model: formValues.model.trim(),
        input_cost_per_1k_tokens: Number(formValues.input_cost_per_1k_tokens),
        output_cost_per_1k_tokens: Number(formValues.output_cost_per_1k_tokens),
        currency: formValues.currency || 'USD',
        active: formValues.active,
        // Siempre explícito: lo que root ve en el switch es lo que queda.
        requires_paid_plan: formValues.requires_paid_plan ?? modelRequiresPaidPlan(formValues.model, null)
      };
      if (formValues.cached_input_cost_per_1k_tokens !== '') {
        payload.cached_input_cost_per_1k_tokens = Number(formValues.cached_input_cost_per_1k_tokens);
      }
      if (formValues.cache_write_cost_per_1k_tokens !== '') {
        payload.cache_write_cost_per_1k_tokens = Number(formValues.cache_write_cost_per_1k_tokens);
      }
      if (formValues.reasoning_token_cost_per_1k_tokens !== '') {
        payload.reasoning_token_cost_per_1k_tokens = Number(formValues.reasoning_token_cost_per_1k_tokens);
      }
      await CreditsEngineService.upsertModelPricing(payload);
      setSnackbar({ open: true, message: 'Precio guardado correctamente.', severity: 'success' });
      setFormOpen(false);
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo guardar el precio.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // No existe DELETE en credits-engine (tabla versionada, auditada) -- en
  // vez de borrar, se crea una nueva versión con `active` invertido.
  const handleToggleActive = async (item) => {
    try {
      setToggling(item.provider_model);
      await CreditsEngineService.upsertModelPricing(toggledPricingPayload(item));
      setSnackbar({
        open: true,
        message: item.active !== false ? 'Precio desactivado.' : 'Precio reactivado.',
        severity: 'success'
      });
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo cambiar el estado.', severity: 'error' });
    } finally {
      setToggling(null);
    }
  };

  const { pageRows, paginationProps } = useTablePagination(pricing, {
    storageKey: 'billingModelPricing',
    hasMore: Boolean(cursor),
    isLoadingMore,
    onNeedMore: loadMore
  });

  return (
    <PageContainer
      title="Precios de IA"
      description="Costo por 1000 tokens (input/output) de cada modelo, usado para calcular el costo real de cada invocación y su equivalente en créditos. Cada guardado crea una nueva versión vigente desde ahora."
      actions={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading}
            sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
            Nuevo precio
          </Button>
        </Stack>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      <Section title="Modelos configurados" subtitle="Precios en USD por 1000 tokens, por versión (más reciente primero).">
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
          </Box>
        ) : pricing.length === 0 ? (
          <Typography sx={{ color: 'var(--wb-muted-70)', py: 3, textAlign: 'center' }}>
            Todavía no hay precios configurados — se está usando el fallback interno del runtime.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Proveedor</TableCell>
                  <TableCell sx={headSx}>Modelo</TableCell>
                  <TableCell sx={headSx} align="right">Input / 1K tokens</TableCell>
                  <TableCell sx={headSx} align="right">Output / 1K tokens</TableCell>
                  <TableCell sx={headSx}>Estado</TableCell>
                  <TableCell sx={headSx}>Disponible en</TableCell>
                  <TableCell sx={headSx}>Vigente desde</TableCell>
                  <TableCell sx={headSx} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((item) => (
                  <TableRow key={`${item.provider_model}#${item.effective_from}`} hover>
                    <TableCell sx={cellSx}>{item.provider}</TableCell>
                    <TableCell sx={cellSx}>{item.model}</TableCell>
                    <TableCell sx={cellSx} align="right">${toNumber(item.input_cost_per_1k_tokens).toFixed(4)}</TableCell>
                    <TableCell sx={cellSx} align="right">${toNumber(item.output_cost_per_1k_tokens).toFixed(4)}</TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        size="small"
                        label={item.active !== false ? 'ACTIVO' : 'INACTIVO'}
                        sx={{
                          backgroundColor: item.active === false ? 'var(--wb-danger-16)' : 'var(--wb-x-bg-22c55e29, rgba(34,197,94,0.16))',
                          color: item.active === false ? 'var(--wb-danger)' : 'var(--wb-success)'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {modelRequiresPaidPlan(item.model, item) ? 'Planes de pago' : 'Todos los planes'}
                      {item.requires_paid_plan == null && (
                        <Typography component="span" sx={{ color: 'var(--wb-text-soft)', fontSize: '0.75rem', ml: 0.5 }}>(por familia)</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>{item.effective_from || '—'}</TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Tooltip title="Editar (crea nueva versión)">
                        <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'var(--wb-blue)' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={item.active !== false ? 'Desactivar' : 'Reactivar'}>
                        <span>
                          <Switch
                            size="small"
                            checked={item.active !== false}
                            disabled={toggling === item.provider_model}
                            onChange={() => handleToggleActive(item)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pricing.length > 0 && <TablePaginationBar {...paginationProps} />}
      </Section>

      <Dialog open={formOpen} onClose={() => !isSaving && setFormOpen(false)} PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface-alt)', color: 'var(--wb-text)', minWidth: { xs: '90%', sm: 480 } } }}>
        <DialogTitle>{isEditing ? 'Editar precio (nueva versión)' : 'Nuevo precio de modelo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Proveedor"
              value={formValues.provider}
              onChange={handleChange('provider')}
              disabled={isEditing}
              placeholder="bedrock"
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-de-ia" anchor="proveedor" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Modelo"
              value={formValues.model}
              onChange={handleChange('model')}
              disabled={isEditing}
              placeholder="us.anthropic.claude-sonnet-5-5"
              helperText={isEditing ? undefined : 'ID exacto con el que se invoca Bedrock (perfil us.), igual al del agente.'}
              FormHelperTextProps={{ sx: { color: 'var(--wb-text-soft)' } }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-de-ia" anchor="modelo" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Costo input por 1000 tokens (USD)"
              value={formValues.input_cost_per_1k_tokens}
              onChange={handleChange('input_cost_per_1k_tokens')}
              type="number"
              inputProps={{ step: '0.0001', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-de-ia" anchor="costo-input-por-1000-tokens-usd" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Costo output por 1000 tokens (USD)"
              value={formValues.output_cost_per_1k_tokens}
              onChange={handleChange('output_cost_per_1k_tokens')}
              type="number"
              inputProps={{ step: '0.0001', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-de-ia" anchor="costo-output-por-1000-tokens-usd" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Costo de lectura de caché por 1000 tokens (opcional)"
              value={formValues.cached_input_cost_per_1k_tokens}
              onChange={handleChange('cached_input_cost_per_1k_tokens')}
              type="number"
              inputProps={{ step: 'any', min: 0 }}
              helperText="Tokens que el modelo lee de la caché de prompt. Vacío: se cobran al costo de input."
              FormHelperTextProps={{ sx: { color: 'var(--wb-text-soft)' } }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-de-ia" anchor="costo-input-cacheado-por-1000-tokens-opcional" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Costo de escritura en caché por 1000 tokens (opcional)"
              value={formValues.cache_write_cost_per_1k_tokens}
              onChange={handleChange('cache_write_cost_per_1k_tokens')}
              type="number"
              inputProps={{ step: 'any', min: 0 }}
              helperText="Tokens que se guardan en la caché de prompt. Vacío: se cobran a 1.25 × el costo de input."
              FormHelperTextProps={{ sx: { color: 'var(--wb-text-soft)' } }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
            />
            <TextField
              label="Costo de tokens de razonamiento por 1000 (opcional)"
              value={formValues.reasoning_token_cost_per_1k_tokens}
              onChange={handleChange('reasoning_token_cost_per_1k_tokens')}
              type="number"
              inputProps={{ step: '0.0001', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-de-ia" anchor="costo-de-tokens-de-razonamiento-por-1000-opcional" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Moneda"
              value={formValues.currency}
              onChange={handleChange('currency')}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="precios-de-ia" anchor="moneda" /></InputAdornment>
              ) }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.active}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, active: e.target.checked }))}
                />
              }
              label="Activo (se muestra en Modelos LLM y se puede elegir en el chat)"
              sx={{ color: 'var(--wb-text)' }}
            />
            {!formValues.active && (
              <Typography variant="caption" sx={{ color: 'var(--wb-text-soft)', mt: -1.5 }}>
                Inactivo: el precio queda guardado, pero el modelo no aparece en el chat ni en Modelos LLM. Úsalo para
                modelos que todavía no están habilitados en la cuenta de AWS.
              </Typography>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.requires_paid_plan ?? modelRequiresPaidPlan(formValues.model, null)}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, requires_paid_plan: e.target.checked }))}
                />
              }
              label="Solo planes de pago (las cuentas gratuitas no pueden elegirlo en el chat)"
              sx={{ color: 'var(--wb-text)' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={isSaving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : null}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success' ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning' ? 'var(--wb-warning-90)'
                  : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

const BillingPricing = () => (
  <RequireBillingRole allow="root" title="Precios de IA">
    <BillingPricingContent />
  </RequireBillingRole>
);

export default BillingPricing;
